import { SavWriter } from 'savfilewriter';
import { describeQuestionTargets } from './interviewerTargets.js';

const STATUS_LABELS = {
  completed: 'Завершён',
  approved: 'Одобрен',
  pending: 'Ожидает',
  rejected: 'Отклонён',
  disqualified: 'Дисквалифицирован',
  under_review: 'На проверке'
};

function utf8Truncate(value, maxBytes) {
  const text = value == null ? '' : String(value);
  const encoder = new TextEncoder();
  let used = 0;
  let out = '';
  for (const char of text) {
    const size = encoder.encode(char).length;
    if (used + size > maxBytes) break;
    out += char;
    used += size;
  }
  return out;
}

function utf8Size(value) {
  return new TextEncoder().encode(value == null ? '' : String(value)).length;
}

function optionText(option) {
  if (option == null) return '';
  if (typeof option === 'string' || typeof option === 'number') return String(option).trim();
  if (typeof option === 'object') {
    return String(option.text ?? option.label ?? option.value ?? '').trim();
  }
  return '';
}

function questionOptions(question) {
  const plain = Array.isArray(question && question.options) ? question.options.map(optionText) : [];
  const images = Array.isArray(question && question.imageOptions)
    ? question.imageOptions.map(optionText)
    : [];
  return [...plain, ...images].filter(Boolean);
}

function respondentLabel(response, index, hideIdentity) {
  if (!response.userId || hideIdentity) {
    return !response.userId && !hideIdentity
      ? 'Анонимно'
      : `Респондент ${index + 1}`;
  }
  const user = response.user || {};
  return user.fullName || user.email || `Респондент ${index + 1}`;
}

function profileText(response, hideIdentity) {
  const user = response.user || {};
  if (!response.userId) return hideIdentity ? '' : 'Анонимный ответ';
  const parts = hideIdentity
    ? [user.age ? `${user.age} лет` : '', user.gender || '', user.city || '', user.region || '']
    : [
      user.fullName || '',
      user.email || '',
      user.age ? `${user.age} лет` : '',
      user.gender || '',
      user.city || '',
      user.region || ''
    ];
  return parts.filter(Boolean).join(', ');
}

function statusText(status) {
  return STATUS_LABELS[status] || status || '';
}

function numericLabels(labels) {
  return labels.reduce((map, label, index) => {
    map[String(index + 1)] = utf8Truncate(label, 250);
    return map;
  }, {});
}

function codeForLabel(labels, value) {
  const index = labels.findIndex((label) => label === value);
  return index >= 0 ? index + 1 : null;
}

export function buildSurveySav(survey, responses, options = {}) {
  const hideIdentity = !!options.hideRespondentIdentity;
  const showInterviewers = !!options.showInterviewers && !hideIdentity;
  const questions = Array.isArray(survey && survey.questions) ? survey.questions : [];
  const rows = Array.isArray(responses) ? responses : [];
  const usedShort = new Set();
  const columns = [];

  const add = (preferred, spec) => {
    let name = String(preferred || 'V').replace(/[^A-Za-z0-9_]/g, '');
    if (!/^[A-Za-z]/.test(name)) name = `V${name}`;
    let short = name.slice(0, 8).toUpperCase();
    if (!name || usedShort.has(short)) {
      let n = columns.length + 1;
      do {
        name = `V${String(n).padStart(7, '0')}`;
        short = name.slice(0, 8).toUpperCase();
        n += 1;
      } while (usedShort.has(short));
    }
    usedShort.add(short);
    columns.push({ ...spec, name: name.slice(0, 64) });
  };

  add('n', {
    type: 0,
    label: 'Номер',
    printFormat: 'F8.0',
    get: (_response, index) => index + 1
  });
  add('resp', {
    type: 80,
    label: 'Респондент',
    get: (response, index) => utf8Truncate(respondentLabel(response, index, hideIdentity), 80)
  });
  add('profile', {
    type: 255,
    label: 'Данные респондента',
    get: (response) => utf8Truncate(profileText(response, hideIdentity), 255)
  });
  add('completed', {
    type: 32,
    label: 'Дата',
    get: (response) => (
      response.completedAt
        ? utf8Truncate(new Date(response.completedAt).toLocaleString(), 32)
        : ''
    )
  });
  add('timesec', {
    type: 0,
    label: 'Время (сек)',
    printFormat: 'F8.0',
    get: (response) => (
      typeof response.timeSpent === 'number' && Number.isFinite(response.timeSpent)
        ? response.timeSpent
        : null
    )
  });
  add('status', {
    type: 40,
    label: 'Статус',
    get: (response) => utf8Truncate(statusText(response.status), 40)
  });
  if (showInterviewers) {
    add('intrview', {
      type: 120,
      label: 'Интервьюер',
      get: (response) => utf8Truncate(response.interviewerName || '', 120)
    });
  }

  questions.forEach((question, questionIndex) => {
    const questionId = question.id || `q_${questionIndex}`;
    const base = `q${questionIndex + 1}`;
    const text = utf8Truncate(question.text || `Вопрос ${questionIndex + 1}`, 220);
    const answerOf = (response) => response.answers?.[questionId];

    if (question.type === 'multiple' || question.type === 'multiple-image') {
      const labels = questionOptions(question);
      labels.forEach((label, optionIndex) => {
        add(`${base}o${optionIndex + 1}`, {
          type: 0,
          label: utf8Truncate(`${text}: ${label}`, 250),
          printFormat: 'F8.0',
          values: { 0: 'Нет', 1: 'Да' },
          get: (response) => {
            const answer = answerOf(response);
            if (!Array.isArray(answer)) return null;
            return answer.includes(label) ? 1 : 0;
          }
        });
      });
      add(`${base}oth`, {
        type: 255,
        label: utf8Truncate(`${text}: свой вариант`, 250),
        get: (response) => {
          const answer = answerOf(response);
          if (!Array.isArray(answer)) return '';
          const known = new Set(labels);
          return utf8Truncate(answer.filter((item) => !known.has(item)).join(', '), 255);
        }
      });
      return;
    }

    if (question.type === 'ranking') {
      const labels = questionOptions(question);
      labels.forEach((label, optionIndex) => {
        add(`${base}r${optionIndex + 1}`, {
          type: 0,
          label: utf8Truncate(`${text}: место «${label}»`, 250),
          printFormat: 'F8.0',
          get: (response) => {
            const answer = answerOf(response);
            if (!Array.isArray(answer)) return null;
            const place = answer.indexOf(label);
            return place >= 0 ? place + 1 : null;
          }
        });
      });
      return;
    }

    if (question.type === 'matrix') {
      const matrixRows = Array.isArray(question.matrixRows) ? question.matrixRows.map(optionText).filter(Boolean) : [];
      const matrixColumns = (question.matrixColumns || question.matrixCols || []).map(optionText).filter(Boolean);
      matrixRows.forEach((row, rowIndex) => {
        add(`${base}m${rowIndex + 1}`, {
          type: 0,
          label: utf8Truncate(`${text} / ${row}`, 250),
          printFormat: 'F8.0',
          values: numericLabels(matrixColumns),
          get: (response) => {
            const answer = answerOf(response);
            if (!answer || typeof answer !== 'object' || Array.isArray(answer)) return null;
            return codeForLabel(matrixColumns, answer[row]);
          }
        });
      });
      return;
    }

    if (['single', 'single-image', 'dropdown', 'likert'].includes(question.type)) {
      const labels = question.type === 'likert'
        ? (Array.isArray(question.likertOptions) && question.likertOptions.length
          ? question.likertOptions.map(optionText)
          : ['Полностью не согласен', 'Скорее не согласен', 'Нейтрально', 'Скорее согласен', 'Полностью согласен'])
        : questionOptions(question);
      add(base, {
        type: 0,
        label: text,
        printFormat: 'F8.0',
        values: numericLabels(labels),
        get: (response) => codeForLabel(labels, answerOf(response))
      });
      return;
    }

    if (['scale', 'nps', 'rating'].includes(question.type)) {
      add(base, {
        type: 0,
        label: text,
        printFormat: 'F8.0',
        get: (response) => {
          const answer = answerOf(response);
          return typeof answer === 'number' && Number.isFinite(answer) ? answer : null;
        }
      });
      return;
    }

    add(base, {
      type: 255,
      label: text,
      get: (response) => {
        const answer = answerOf(response);
        if (question.type === 'file') {
          if (answer && typeof answer === 'object' && answer.originalName) return utf8Truncate(answer.originalName, 255);
          return answer ? 'Файл загружен' : '';
        }
        if (Array.isArray(answer)) return utf8Truncate(answer.join(', '), 255);
        if (answer && typeof answer === 'object') return utf8Truncate(JSON.stringify(answer), 255);
        return utf8Truncate(answer == null ? '' : String(answer), 255);
      }
    });
  });

  if (showInterviewers) {
    const extra = Array.isArray(survey.interviewerQuestions) ? survey.interviewerQuestions : [];
    extra.forEach((question, index) => {
      const target = describeQuestionTargets(question, survey.interviewers);
      const label = utf8Truncate(`Доп. вопрос: ${question.text || ''}${question.archived ? ' (архив)' : ''} (${target})`, 250);
      add(`iq${index + 1}`, {
        type: 255,
        label,
        get: (response) => {
          const answer = response.interviewerAnswers?.[question.id];
          if (Array.isArray(answer)) return utf8Truncate(answer.join(', '), 255);
          return utf8Truncate(answer == null ? '' : String(answer), 255);
        }
      });
    });
  }

  columns.forEach((column) => {
    if (column.type > 0) {
      const widest = rows.reduce((max, response, index) => {
        return Math.max(max, utf8Size(column.get(response, index)));
      }, 1);
      column.type = Math.min(255, Math.max(column.type, widest));
    }
  });

  const records = rows.map((response, index) => {
    const record = {};
    columns.forEach((column) => {
      record[column.name] = column.get(response, index);
    });
    return record;
  });

  return SavWriter.write({
    encoding: 'UTF-8',
    sysvars: columns.map(({ name, type, label, values, printFormat }) => ({
      name,
      type,
      label: utf8Truncate(label, 250),
      values,
      printFormat: type === 0 ? (printFormat || 'F8.0') : undefined
    }))
  }, records, { fileLabel: 'ThinkLink' });
}

export function downloadSurveySav(survey, responses, options = {}) {
  const buffer = buildSurveySav(survey, responses, options);
  const blob = new Blob([buffer], { type: 'application/x-spss-sav' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `survey_${survey && survey.id ? survey.id : 'responses'}.sav`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
